import { ActionIcon } from './ActionIcon';
import { Button } from './Button';
import { CommonModule } from '@angular/common';
import { Component, Input, output, input } from '@angular/core';
import { IntegrationApp } from './IntegrationApp';
import { IntegrationConnection } from './IntegrationConnection';
import { IntegrationStatus } from './IntegrationStatus';
import { Toggle } from './Toggle';
import { AppIntegration, AppIntegrationFromTo } from "./DashboardIntegrations";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { PackageThisMonth, UserAPI, UserCombi, UserCombiPackage } from "../model";
import { formatHours, unitKey } from "../util/timesaved.util";
import {
  IconDropdownItemUsercombiComponent
} from "../pages/authenticated/dashboard/icon-dropdown-item-usercombi/icon-dropdown-item-usercombi.component";
import { environment } from "../../environments/environment";
import { UserService } from "../services/user/user.service";
import {
  IconDropdownItemUsercombiQueueComponent
} from "../pages/authenticated/dashboard/icon-dropdown-item-usercombi-queue/icon-dropdown-item-usercombi-queue.component";
import { ModalService } from "../modules/shared/modal/modal.service";
import { reconnectUserAPI } from "../modules/userapi-modal/reconnect-userapi.util";
import { OAuthService } from "../services/oauth/oauth.service";
import { CombiPackageService } from "../services/combipackage/combipackage.service";
import { LocalStorageService } from "../local-storage/local-storage.service";
import { Router } from '@angular/router';
import { logConsiderServiceLevelRecommendation } from "../util/analytics.util";
import { ConfirmModalComponent } from "../modules/shared/confirm-modal/confirm-modal.component";

@Component({
  selector: 'IntegrationGridItem',
  imports: [
    ActionIcon,
    Button,
    CommonModule,
    IntegrationApp,
    IntegrationConnection,
    IntegrationStatus,
    Toggle,
    IntegrationApp,
    TranslatePipe,
    IconDropdownItemUsercombiComponent,
    IconDropdownItemUsercombiQueueComponent
  ],
  template: `
    @if (href) {
      <a
          class="my-button flex transform hover:scale-105 transition-transform duration-200 group"
          href="{{ href }}"
      >
        <ng-container *ngTemplateOutlet="card"></ng-container>
      </a>
    } @else {

      @if (!hasClickHandler()) {
        <div>
          <ng-container *ngTemplateOutlet="card"></ng-container>
        </div>
      }
      @if (hasClickHandler()) {
        <div
            class="flex transform hover:scale-105 transition-transform duration-200 group cursor-pointer"
            (click)="onClicked()">
          <ng-container *ngTemplateOutlet="card"></ng-container>
        </div>
      }
    }

    <ng-template #card>
      <div class="flex flex-col bg-white shadow-soft rounded-3xl overflow-hidden w-full h-full">
        <div class="flex items-center divide-x divide-gray-100 border-b border-gray-100">
          <IntegrationApp
              class="w-1/2 self-stretch {{
                integration?.status !== 'trial-ended' && integration?.status !== 'installing' && integration?.userCombiPackage?.active !== false && integration?.userCombiPackage?.fromUserAPI?.connected !== false ? '' : 'grayscale'
              }}"
              [active]="integration?.userCombiPackage?.active !== false && integration?.userCombiPackage?.fromUserAPI?.connected !== false"
              [app]="firstApp()"
              [description]="firstApp().userApiName"
          ></IntegrationApp>
          <IntegrationConnection
              [active]="integration?.userCombiPackage?.active !== false"
              class="w-12 h-12 -mx-6 !border-0 z-10 {{
                integration?.status !== 'trial-ended' && integration?.status !== 'installing' && integration?.userCombiPackage?.active !== false && integration?.userCombiPackage?.fromUserAPI?.connected !== false ? '' : 'grayscale'
              }}"
              svgClass="w-6 h-3.5"
          ></IntegrationConnection>
          <IntegrationApp
              class="w-1/2 self-stretch {{
                integration?.status !== 'trial-ended' && integration?.status !== 'installing' && integration?.userCombiPackage?.active !== false && integration?.userCombiPackage?.toUserAPI?.connected !== false ? '' : 'grayscale'
              }}"
              [active]="integration?.userCombiPackage?.active !== false && integration?.userCombiPackage?.toUserAPI?.connected !== false"
              [app]="secondApp()"
              [description]="secondApp()?.userApiName"
          ></IntegrationApp>
        </div>

        <!-- what Combidesk did this month (COMBI-4629) -->
        @if (showThisMonth(integration?.thisMonth)) {
          <div data-test="this-month" class="bg-blue-50 border-b border-blue-100 px-5 py-2.5 flex flex-col gap-0.5">
            <span class="flex items-start gap-1.5 text-xs font-bold text-blue-600">
              <!-- wrapper is exactly one text line high, so the icon stays centered on the first line when the text wraps -->
              <span class="flex h-[1lh] items-center flex-none"><svg aria-hidden="true" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"></path></svg></span>
              {{ 'PAGE_DASHBOARD_TIMESAVED_CARD_THIS_MONTH' | translate: {items: thisMonthItems(integration.thisMonth)} }}
            </span>
            <span class="text-xs text-gray-600 pl-5">{{ 'PAGE_DASHBOARD_TIMESAVED_CARD_HOURS' | translate: {hours: thisMonthHours(integration.thisMonth)} }}</span>
          </div>
        }

        @if (!readonly()) {
          <div
              class="p-5 pt-3 flex-grow"
          >
            @if (!integration?.status) {
              <div class="flex flex-row">
                <div class="flex flex-col">
                  @if (integration?.specialRecommendation) {
                    <div class="bg-yellow-400 text-white rounded-full text-xs px-3 py-1 font-semibold mb-2 mr-auto">
                      {{ 'PAGE_DASHBOARD_LABEL_SPECIAL_RECOMMENDATION' | translate }}
                    </div>
                  }
                  <p
                      class="text-xxs text-gray-400 leading-normal mb-0 pr-5 align-middle"
                      [innerHTML]="description() || integration.description">
                  </p>
                </div>

                @if (showTryNowButton()) {
                  <Button
                      color="blue"
                      [shadow]="false"
                      size="xsmall"
                      (click)="onClicked()"
                  >
                    {{ 'PAGE_DASHBOARD_BUTTON_TRYNOW' | translate }}
                  </Button>
                }

              </div>
            }

            @if (integration?.status) {
              <div class="flex flex-col {{
                integration.status === 'trial-running' || integration.status === 'trial-ended' ? 'gap-4' : 'gap-2.5'
              }}">
                <div class="flex flex-wrap gap-2 items-center justify-between">
                  @if (integration?.status && integration.status !== 'error' && integration.status !== 'installing') {
                    <IntegrationStatus
                        [integration]="integration"
                        messageClass="text-xxs mb-0"
                    ></IntegrationStatus>
                  }
                  @if (integration.userCombiPackage.requestedDeletionDate) {
                    <div class="text-xxs italic">{{ 'PAGE_DASHBOARD_DELETIONDATE_HINT' | translate }} {{ integration.userCombiPackage.requestedDeletionDate }}</div>
                  } @else if (integration.userCombiPackage.combideskAppStorePackage && (integration.status === 'trial-running' || integration.status === 'trial-ended')) {
                    <div class="flex-none">
                      <Button
                          [color]="integration.status === 'trial-ended' ? 'red' : 'blue'"
                          [shadow]="false"
                          size="xsmall"
                          (click)="onOrderButtonClicked()"
                      >
                        {{ 'PAGE_DASHBOARD_BUTTON_ORDERNOW' | translate }}
                      </Button>
                    </div>
                  }
                </div>

                @if (integration?.historyProgress) {
                  <div class="my-1">
                    <div class="flex justify-between text-xxs text-gray-500 mb-0.5">
                      <span>{{ 'PAGE_DASHBOARD_USERCOMBI_CATCHUP_PROGRESS' | translate: { processed: integration.historyProgress.processed, total: integration.historyProgress.total } }}</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-1.5">
                      <div class="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                           [style.width.%]="integration.historyProgress.processed / integration.historyProgress.total * 100"></div>
                    </div>
                  </div>
                }

                <div class="flex items-center justify-between">
                  @if (integration.userCombiPackage.serviceLevel === 'GREEN') {
                    <div class="text-xxs">
                      <a [href]="('SUPERFOOTER_LINK_ABOUT_COMBIDESK_URL' | translate) + '?deeplink=sustainability'"
                         class="underline text-seagreen" [innerHTML]="'SERVICE_LEVEL_GREEN' | translate"> </a>
                    </div>
                  }
                  @if (getRecommendedServiceLevel(integration) !== undefined) {
                    <div>
                      <div class="text-xxs bg-yellow-100 border-yellow-200 border-[1px] text-gray-600 rounded-xl py-1 px-2.5">
                        <span (click)="editServiceLevel()" [innerHTML]="'PAGE_DASHBOARD_USERCOMBIPACKAGE_SWITCH_SERVICE_LEVEL' | translate: {serviceLevel: 'SERVICE_LEVEL_' + getRecommendedServiceLevel(integration) | translate }"></span><br/>
                      </div>
                      <div style="font-size: 8.5px" class="my-2" [innerHTML]="'PAGE_DASHBOARD_USERCOMBIPACKAGE_SWITCH_SERVICE_LEVEL_BASED_ON' | translate: { syncPercentage: Math.round(integration.userCombiPackage.syncPercentage) }">
                      </div>
                    </div>
                  }
                  @if (this.showPercentages()) {
                    <div class="text-xxs">
                      @if (integration.userCombiPackage.successPercentage !== undefined) {
                        <div
                            [class]="integration.userCombiPackage.successPercentage < 90 ? 'cursor-pointer text-seagreen' : ''">
                          {{ integration.userCombiPackage.successPercentage }}% succesvolle syncs
                        </div>
                      }
                    </div>
                  }

                  @if (integration.userCombiPackage?.canBePaused && integration.status !== 'installing') {
                    <Toggle
                        [active]="integration.userCombiPackage.active"
                        (toggled)="onToggleActiveClicked()"
                    >
                      >
                    </Toggle>
                  }

                  <!-- forces the icons to the right in case there are no icons to the left -->
                  <span></span>

                  <div class="flex items-center gap-3.5 ml-2 text-gray-400">
                    @if (integration?.status !== 'error' && (integration.userCombiPackage.status === 'TRIAL' || integration.userCombiPackage.status === 'INSTALLED') && integration.userCombiPackage.executableUserCombis.length > 0 && integration.userCombiPackage.fromUserAPI?.connected && integration.userCombiPackage.toUserAPI?.connected) {
                      <ActionIcon type="run" tooltip="PAGE_DASHBOARD_LABEL_RUN">
                        @for (userCombi of removeIntervalCombis(integration.userCombiPackage.executableUserCombis); track userCombi.id; let first = $first; let last = $last) {
                          <li (click)="onRunClicked(userCombi)" [class.rounded-t-2xl]="first"
                              [class.rounded-b-2xl]="last" class="dropdown-grid-item">
                            <app-icon-dropdown-item-usercombi
                                [userCombi]="userCombi"></app-icon-dropdown-item-usercombi>
                          </li>
                        }
                      </ActionIcon>
                    }

                    @if (integration.status !== 'installing') {
                      @if (showHistoryButton() && hasUserCombisWithHistory(integration.userCombiPackage)) {
                        <div>
                          @if (hasUserCombiWithFailedItems(integration.userCombiPackage)) {
                            <div class="left-1.5 relative rounded-full flex justify-center items-center">
                              <span
                                  class="-mt-2 inline-block rounded-full border-4 text-xs ml-1 border-combidesk-red animate-pulse"></span>
                            </div>
                          }
                          <ActionIcon type="list" tooltip="PAGE_DASHBOARD_LABEL_HISTORY">
                            @for (userCombi of getUserCombisWithHistory(integration.userCombiPackage); track userCombi.id; let first = $first; let last = $last) {
                              <li (click)="onHistoryClicked(userCombi)" [class.rounded-t-2xl]="first"
                                  [class.rounded-b-2xl]="last" class="dropdown-grid-item">
                                <app-icon-dropdown-item-usercombi
                                    [userCombi]="userCombi"></app-icon-dropdown-item-usercombi>
                              </li>
                            }
                          </ActionIcon>
                        </div>
                      }
  
                      @if (!integration.userCombiPackage.abandonedCartPackage && hasUserCombisWithHistory(integration.userCombiPackage)) {
                        <ActionIcon type="stats" tooltip="PAGE_DASHBOARD_LABEL_STATISTICS"
                                    (clicked)="onStatisticsClicked()"></ActionIcon>
                      }
  
                      @if (integration.userCombiPackage.abandonedCartPackage) {
                        <ActionIcon type="stats" tooltip="PAGE_DASHBOARD_LABEL_STATISTICS"
                                    (clicked)="onAbandonedCartStatisticsClicked()"></ActionIcon>
                      }

                      @if (getUserCombisSupportingQueueSizeCalculation(integration.userCombiPackage).length > 0) {
                        <ActionIcon type="queue" tooltip="PAGE_DASHBOARD_LABEL_QUEUE_SIZE">
                          @for (userCombi of getUserCombisSupportingQueueSizeCalculation(integration.userCombiPackage); track userCombi.id; let first = $first; let last = $last) {
                            <li (click)="onCalculateQueueSizeClicked(userCombi)" [class.rounded-t-2xl]="first"
                                [class.rounded-b-2xl]="last" class="dropdown-grid-item">
                              <app-icon-dropdown-item-usercombi-queue
                                  [userCombi]="userCombi"></app-icon-dropdown-item-usercombi-queue>
                            </li>
                          }
                        </ActionIcon>
                      }

                      @if (showConfigCheckButton()) {
                        <ActionIcon type="ai-check"
                                    tooltip="AI Config Check"
                                    (clicked)="onConfigCheckClicked()">
                        </ActionIcon>
                      }

                      <ActionIcon type="edit"
                                  tooltip="PAGE_DASHBOARD_LABEL_EDIT"
                                  (clicked)="onEditClicked()">
                      </ActionIcon>
  
                    }
                    @if (integration.userCombiPackage.canBeUninstalled) {
                      @if (integration.userCombiPackage.requestedDeletionDate) {
                        <ActionIcon type="restore"
                                    tooltip="PAGE_DASHBOARD_LABEL_RESTORE"
                                    (clicked)="onRestoreClicked()">
                        </ActionIcon>
                      } @else {
                        <ActionIcon type="trash"
                                    tooltip="PAGE_DASHBOARD_LABEL_DELETE"
                                    (clicked)="onDeleteClicked()">
                        </ActionIcon>
                      }
                    }
                  </div>
                </div>
              </div>
            }
          </div>
        }

        @if (integration?.status === 'error' && integration.errorType === 'awaiting_payment') {
          <div class="flex flex-row gap-2 bg-red-500 text-white pb-3 px-4">
            <div class="text-xxs flex-grow mt-3" [innerHTML]="integration.message"></div>
            <Button
                class="pt-2"
                color="white"
                [outline]="true"
                size="xsmall"
                href="/accountsettings?deeplink=invoicing"
            >
              {{ 'PAGE_DASHBOARD_BUTTON_PAY_INVOICE' | translate }}
            </Button>
          </div>
        }

        @if ((integration?.status === 'error' && integration.errorType === 'userapi_disconnected') || (integration?.status !== 'installing' && integration?.userCombiPackage && (!integration.userCombiPackage.fromUserAPI?.connected || !integration.userCombiPackage.toUserAPI?.connected))) {
          <div class="flex flex-row gap-2 bg-red-500 text-white pb-3 px-4">
            <div class="text-xxs flex-grow mt-3"
                 [innerHTML]="'PAGE_DASHBOARD_USERAPI_DISCONNECTED' | translate: { api: !integration.userCombiPackage.fromUserAPI?.connected ? integration.userCombiPackage.fromAPI.api.name : integration.userCombiPackage.toAPI.api.name }"></div>
            @if (!integration.userCombiPackage.fromUserAPI?.connected ? integration.userCombiPackage.fromAPI.api.allowReconnect : integration.userCombiPackage.toAPI.api.allowReconnect) {
              <Button
                  class="pt-2"
                  color="white"
                  [outline]="true"
                  size="xsmall"
                  (click)="onReconnectButtonClicked(!integration.userCombiPackage.fromUserAPI?.connected ? integration.userCombiPackage.fromUserAPI : integration.userCombiPackage.toUserAPI)"
              >
                {{ 'PAGE_DASHBOARD_BUTTON_RECONNECT' | translate }}
              </Button>
            }
          </div>
        }

        @if (integration?.status === 'installing') {
          <div class="flex flex-row gap-2 bg-gray-300 text-white pb-3 px-4">
            @if (integration.userCombiPackage?.fromUserAPI?.api.configType === 'RABOBANK' && !integration.userCombiPackage?.fromUserAPI?.privateKey) {
              <div class="text-xxs flex-grow mt-3" [innerHTML]="'PAGE_DASHBOARD_INSTALLING_RABOBANK' | translate"></div>
            } @else {
              <div class="text-xxs flex-grow mt-3" [innerHTML]="'PAGE_DASHBOARD_INSTALLING' | translate"></div>
              <Button
                  class="pt-2"
                  color="white"
                  [outline]="true"
                  size="xsmall"
                  (click)="onEditClicked()">
                {{ 'PAGE_DASHBOARD_BUTTON_CONTINUE_INSTALLING' | translate }}
              </Button>
            }
          </div>
        }

      </div>
    </ng-template>
  `
})
export class IntegrationGridItem {
  readonly firstApp = input<AppIntegrationFromTo>();
  readonly secondApp = input<AppIntegrationFromTo>();
  readonly description = input('');
  readonly showTryNowButton = input(true);
  @Input() href = '';
  readonly hasClickHandler = input(false);
  readonly readonly = input(false);
  readonly showHistoryButton = input(true);
  @Input() integration?: AppIntegration;
  readonly status = input('');
  clicked = output<void>();
  orderButtonClicked = output<void>();
  toggleActiveClicked = output<void>();
  runClicked = output<UserCombi>();
  historyClicked = output<UserCombi>();
  statisticsClicked = output<void>();
  calculateQueueSizeClicked = output<UserCombi>();
  abandonedCartStatisticsClicked = output<void>();
  editClicked = output<void>();
  deleteClicked = output<void>();
  restoreClicked = output<void>();
  deeplinkClicked = output<string>();
  configCheckClicked = output<void>();
  recommendedServiceLevel: string;

  constructor(private userService: UserService,
              private router: Router,
              private oAuthService: OAuthService,
              private combiPackageService: CombiPackageService,
              private localStorageService: LocalStorageService,
              private translate: TranslateService,
              private modalService: ModalService<any>) {
  }

  onClicked(): void {
    this.clicked.emit();
  }

  /** "412 facturen · 4 creditnota's": items of different units are never added up (COMBI-4629) */
  thisMonthItems(thisMonth: PackageThisMonth): string {
    return thisMonth.units.map(unit => `${unit.count} ${this.translate.instant(unitKey(unit.unit, unit.count))}`).join(' · ');
  }

  /** "Deze maand al 1 factuur · ~0 uur bespaard" says nothing (or backfires), so the line only shows from half an hour */
  showThisMonth(thisMonth: PackageThisMonth | undefined): boolean {
    return thisMonth?.units?.length > 0 && thisMonth.seconds >= 1800;
  }

  thisMonthHours(thisMonth: PackageThisMonth): string {
    return formatHours(thisMonth.seconds, this.translate.getCurrentLang() || 'nl');
  }

  onOrderButtonClicked(): void {
    this.orderButtonClicked.emit();
  }

  async onReconnectButtonClicked(userApi: UserAPI) {
    if (!userApi) {
      this.onEditClicked()
      return;
    }
    const reconnected = await reconnectUserAPI(userApi, {
      userService: this.userService,
      router: this.router,
      oAuthService: this.oAuthService,
      combiPackageService: this.combiPackageService,
      localStorageService: this.localStorageService,
      modalService: this.modalService
    });
    if (reconnected) {
      // let's simply reload the screen, so the status of this integration is updated
      window.location.reload();
    }
  }

  onToggleActiveClicked(): void {
    this.toggleActiveClicked.emit();
  }

  onRunClicked(userCombi: UserCombi) {
    this.runClicked.emit(userCombi);
  }

  onHistoryClicked(userCombi: UserCombi) {
    this.historyClicked.emit(userCombi);
  }

  onStatisticsClicked() {
    this.statisticsClicked.emit();
  }

  onCalculateQueueSizeClicked(userCombi: UserCombi) {
    this.calculateQueueSizeClicked.emit(userCombi);
  }

  onEditClicked() {
    this.editClicked.emit();
  }

  onAbandonedCartStatisticsClicked() {
    this.abandonedCartStatisticsClicked.emit();
  }

  onDeleteClicked() {
    this.deleteClicked.emit();
  }

  async onRestoreClicked() {
    const modalService: ModalService<ConfirmModalComponent> = this.modalService.create(ConfirmModalComponent);
    modalService.component.title = "PAGE_DASHBOARD_RESTORE_CONFIRM_TITLE";
    modalService.component.message = "PAGE_DASHBOARD_RESTORE_CONFIRM_DESCRIPTION";
    modalService.component.confirmButton = "PAGE_DASHBOARD_RESTORE_CONFIRM_BUTTON";

    if (await modalService.open()) {
      this.restoreClicked.emit();
    }
  }

  hasUserCombisWithHistory(userCombiPackage: UserCombiPackage): boolean {
    return this.getUserCombisWithHistory(userCombiPackage).length > 0;
  }

  getUserCombisWithHistory(userCombiPackage: UserCombiPackage): Array<UserCombi> {
    return userCombiPackage.userCombis.filter(uc => uc.combiType !== "ONCE" && this.isInUse(uc));
  }

  getUserCombisSupportingQueueSizeCalculation(userCombiPackage: UserCombiPackage): Array<UserCombi> {
    return userCombiPackage.userCombis.filter(uc => uc.canCalculateQueueSize && this.isInUse(uc));
  }

  /**
   * The history endpoints only return runs of FREE, TRIAL, TRIAL_EXPIRED and ORDERED usercombis, so deleted ones (fi.
   * removed duplicates, COMBI-4527) and ones still being installed are left out of the dropdowns. Inactive usercombis
   * stay listed: their history is still relevant.
   */
  private isInUse(userCombi: UserCombi): boolean {
    return userCombi.status !== "DELETED" && userCombi.status !== "INSTALLING";
  }

  removeIntervalCombis(executableUserCombis: Array<UserCombi>) {
    if (!environment.production || this.userService.isSuperUser()) {
      return executableUserCombis;
    }
    return executableUserCombis.filter(uc => !uc.intervalMinutes);
  }

  hasUserCombiWithFailedItems(userCombiPackage: UserCombiPackage): boolean {
    // only the usercombis in the history list, so the red dot always points at something that can be opened
    return this.getUserCombisWithHistory(userCombiPackage).some(uc => uc.failedExecutionCount > 0);
  }

  showPercentages(): boolean {
    return this.userService.getUser()?.cindyLoper;
  }

  getRecommendedServiceLevel(integration: AppIntegration): string {
    if (integration?.status === 'error') {
      return;
    }
    if (this.recommendedServiceLevel !== undefined) {
      return this.recommendedServiceLevel;
    }
    const userCombiPackage = integration?.userCombiPackage;
    const downsellToGreen = userCombiPackage && userCombiPackage.serviceLevel !== 'GREEN' && userCombiPackage.syncPercentage !== undefined && userCombiPackage.syncPercentage < 25;
    if (downsellToGreen) {
      this.recommendedServiceLevel = 'GREEN';
    }
    const upsellToPro = userCombiPackage && userCombiPackage.serviceLevel === 'BASIC' && userCombiPackage.syncPercentage !== undefined && userCombiPackage.syncPercentage > 80;
    if (upsellToPro) {
      this.recommendedServiceLevel = 'PRO';
    }
    return this.recommendedServiceLevel;
  }

  editServiceLevel() {
    this.deeplinkClicked.emit("ServiceLevel");
    logConsiderServiceLevelRecommendation(this.integration.userCombiPackage, this.recommendedServiceLevel);
  }

  onConfigCheckClicked() {
    this.configCheckClicked.emit();
  }

  showConfigCheckButton(): boolean {
    return this.userService.getUser()?.cindyLoper === true;
  }

  protected readonly Math = Math;
}
